/**
 * @reflecto/core — smsDigest.ts
 *
 * The §3.3 SMS digest primitives: a per-row LEAF and an order-independent BUCKET fold. The phone
 * and each PC compute these independently over the rows they hold; comparing buckets is how a
 * divergence is detected without either side sending a message.
 *
 * INTEROP: this file is the source of truth for the algorithm. `SmsDigestCalculator.kt` must
 * produce byte-identical output, and both sides assert against the same frozen vector file
 * (`test-vectors/sms-digest-v1.json`, copied verbatim into the Android test resources).
 * Divergence = stop-everything: a leaf that differs by one byte makes a bucket mismatch that no
 * amount of repair can ever close, which is a permanent 6-hourly repair loop rather than a bug
 * with an error message.
 *
 * ⚠️ PRIVACY. The `salt` is KEYING MATERIAL — a per-install 32-byte secret delivered inside the
 * E2E envelope. Never log it, never put it in an error message, never commit a real one. Nothing
 * here reads a message BODY: the leaf deliberately hashes only identity and coarse metadata, so
 * the digest is not an oracle for content even if a bucket leaked.
 *
 * SCOPE: leaf and bucket only. The four-level tree walk (root → thread → month → day) lives in
 * `client-core`, and the wire packing of a thread level is S8-4's, together with the wire type it
 * serves — §3.3 specifies a 14-byte thread entry while specifying a 16-byte leaf, and that
 * contradiction is resolved where the byte budget is actually decided, not frozen into a vector
 * here.
 *
 * ---------------------------------------------------------------------------
 * WHY THE ENCODING IS NOT §3.3's.
 *
 * §3.3 writes the leaf input as `providerId | date | type | threadId`. That is prose: it fixes the
 * term set and their order, and says nothing about bytes. Three concrete hazards made the obvious
 * readings unsafe, all of them permanent once a vector ships:
 *
 *  1. DECIMAL-ASCII INTEGERS ARE NOT PORTABLE HERE. `SmsLedgerMessage.row_id` is a TypeScript
 *     `number` and its guard checks only `typeof === "number"`, so a non-integer (`1.5`), an
 *     exponential magnitude (`1e21`) and a value above 2^53 are all admissible at the boundary.
 *     `String(n)` renders each in a form Kotlin's `Long.toString()` can never produce, so the two
 *     sides would hash different bytes for the same wire row. Fixed-width big-endian removes the
 *     formatting question entirely, and the range check below turns "hash something different"
 *     into "refuse", which is visible.
 *  2. SEPARATOR-BASED FRAMING IS ONLY INJECTIVE BY ACCIDENT. A `0x1f` separator relies on no term
 *     containing that byte. It happens to hold — `threadId` is the only free-form term and it is
 *     last — but it holds because of where the term SITS, so appending a fifth term later breaks
 *     it silently. Length-prefixing is injective regardless of content or term order.
 *  3. `type` AS A STRING RE-OPENS AN ENCODING QUESTION for a two-valued field. One byte does not.
 *
 * So the input is fully self-delimiting, with no separators at all:
 *
 *     TAG ‖ be64(providerId) ‖ be64(date) ‖ u8(typeCode) ‖ be32(len(threadId)) ‖ utf8(threadId)
 *
 * §3.3's term set and order are preserved exactly; only the byte-level framing is stated where the
 * spec left it open. The spec carries an amendment recording this.
 */

import { hmacSha256 } from "./filetransfer/crypto";

/**
 * Domain separation, and the algorithm's version. Any change to the encoding, the term set, the
 * truncation or the tag itself is a BREAKING change: it needs a `v2` tag, a new vector file, and a
 * synchronized Kotlin update. Never change one side alone.
 *
 * §3.3's formula has no domain tag. One is added here on the `safetyNumber.ts` precedent — an
 * unkeyed-by-purpose HMAC over four integers is the kind of thing that gets reused elsewhere by
 * accident, and a tag makes that impossible rather than merely unlikely.
 */
export const SMS_DIGEST_DOMAIN_TAG = "reflecto-sms-digest-v1";

/** Bytes of a leaf, after truncation. §3.3: `[0:16]`. */
export const SMS_DIGEST_LEAF_BYTES = 16;

/** Required salt length. A per-install 32-byte random, per §3.3. */
export const SMS_DIGEST_SALT_BYTES = 32;

/** Milliseconds in a UTC day. Fixed-width by definition — no leap seconds in Unix time. */
const MS_PER_DAY = 86_400_000;

const TAG_BYTES = new Uint8Array(Array.from(SMS_DIGEST_DOMAIN_TAG, (ch) => ch.charCodeAt(0)));

/** One row's digest inputs — the §3.3 leaf terms, and nothing else. */
export interface SmsDigestRow {
  /** Provider `_id`. A non-negative safe integer. */
  readonly providerId: number;
  /**
   * The row's provider `date`, in epoch millis — §3.3's `date` term.
   *
   * This is `SmsLedgerMessage.sort_timestamp`, the phone's own paging axis, NOT `timestamp`
   * (service-centre time). The two differ by hours on a delayed delivery, and only the paging axis
   * is a column the phone can bucket by without reading anything else. Naming it `date` here keeps
   * §3.3's vocabulary; the mapping is asserted by the vector fixtures.
   */
  readonly date: number;
  /**
   * §3.3's `type` term, as the NORMALIZED direction rather than the provider's raw TYPE integer.
   *
   * The PC never receives the raw column, so a raw-TYPE leaf would be uncomputable on one side —
   * a permanent mismatch on every outbound row. The accepted cost is that a TYPE transition on an
   * outbound row (queued → sent → failed) leaves the leaf unchanged: the digest answers "is this
   * row present", not "what state is it in".
   */
  readonly type: "in" | "out";
  /** Provider `THREAD_ID`, as the string the wire carries. Free-form; length-prefixed below. */
  readonly threadId: string;
}

function assertSalt(salt: Uint8Array): void {
  if (salt.length !== SMS_DIGEST_SALT_BYTES) {
    // Length only — the salt is keying material and must never reach a message.
    throw new RangeError(
      `sms digest: salt must be ${SMS_DIGEST_SALT_BYTES} bytes, got ${salt.length}`
    );
  }
}

/**
 * Write a non-negative safe integer as 8 bytes, big-endian.
 *
 * REFUSES rather than truncating. A `row_id` above 2^53 has already lost precision at `JSON.parse`
 * before reaching here, so no encoding width can recover it — hashing it anyway would produce a
 * value that silently disagrees with the phone's `Long`. A throw is the honest outcome, and the
 * name of the field is safe to include (it is a field name, not a value).
 */
/**
 * Whether a number is admissible as a be64 digest term — THE definition of that rule.
 *
 * Extracted so a caller can REFUSE a row before reaching [writeBe64], which throws. The client
 * fold needs exactly this predicate: `isSmsLedgerMessage` admits any `typeof === "number"` for
 * `row_id` and `sort_timestamp`, so NaN, a negative, a non-integer and a value above 2^53 all
 * arrive from the wire, and a fold that only checked `typeof` rejected its own promise instead of
 * returning a decision. Sharing the predicate rather than copying it keeps the throw and the
 * refusal describing the same set.
 */
export function isSmsDigestTerm(value: number): boolean {
  return Number.isSafeInteger(value) && value >= 0;
}

function writeBe64(out: Uint8Array, offset: number, value: number, field: string): void {
  if (!isSmsDigestTerm(value)) {
    throw new RangeError(`sms digest: ${field} must be a non-negative safe integer`);
  }
  // Split at 2^32 so both halves stay inside exact float arithmetic.
  const high = Math.floor(value / 0x1_0000_0000);
  const low = value >>> 0 === value ? value : value - high * 0x1_0000_0000;
  for (let i = 0; i < 4; i += 1) {
    out[offset + i] = (high >>> (8 * (3 - i))) & 0xff;
    out[offset + 4 + i] = (low >>> (8 * (3 - i))) & 0xff;
  }
}

function utf8(text: string): Uint8Array {
  // Hand-rolled because `packages/core` compiles against `lib: ["ES2020"]` with no DOM and no
  // @types/node, so `TextEncoder` is not a declared global here. Same reason this file uses
  // libsodium for HMAC rather than WebCrypto.
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    let code = text.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00);
        i += 1;
      }
    }
    // An UNPAIRED surrogate is REFUSED, on both sides, rather than substituted.
    //
    // Substituting is the trap. Every plausible substitution disagrees across the two languages:
    // a hand-rolled encoder naturally emits a 3-byte WTF-8 sequence; Node's `Buffer.from` yields
    // U+FFFD; and the JVM's `String.getBytes(UTF_8)` yields a literal `'?'` (0x3f) — the
    // charset encoder's default replacement BYTE, not the replacement CHARACTER, which is a
    // decoding concept. An earlier revision of this file substituted U+FFFD to "match the JVM"
    // and a frozen vector caught it disagreeing with the JVM within one test run.
    //
    // So this refuses, for the same reason `writeBe64` refuses an out-of-range integer: where the
    // two platforms' defaults differ, picking one and hoping is how a permanent, unrepairable
    // bucket mismatch gets frozen into a vector. Malformed input has no correct hash.
    if (code >= 0xd800 && code <= 0xdfff) {
      throw new RangeError("sms digest: threadId contains an unpaired surrogate");
    }
    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      bytes.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f)
      );
    }
  }
  return new Uint8Array(bytes);
}

/**
 * The exact bytes a leaf is computed over. Exported so a vector can pin the PRE-HASH input, which
 * is what localises a divergence: if the two languages disagree here, the HMAC is innocent.
 */
export function smsDigestLeafInput(row: SmsDigestRow): Uint8Array {
  const threadIdBytes = utf8(row.threadId);
  const out = new Uint8Array(TAG_BYTES.length + 8 + 8 + 1 + 4 + threadIdBytes.length);
  let at = 0;
  out.set(TAG_BYTES, at);
  at += TAG_BYTES.length;
  writeBe64(out, at, row.providerId, "providerId");
  at += 8;
  writeBe64(out, at, row.date, "date");
  at += 8;
  out[at] = row.type === "out" ? 1 : 0;
  at += 1;
  writeBe64Length(out, at, threadIdBytes.length);
  at += 4;
  out.set(threadIdBytes, at);
  return out;
}

function writeBe64Length(out: Uint8Array, offset: number, value: number): void {
  out[offset] = (value >>> 24) & 0xff;
  out[offset + 1] = (value >>> 16) & 0xff;
  out[offset + 2] = (value >>> 8) & 0xff;
  out[offset + 3] = value & 0xff;
}

/** §3.3: `leaf(row) = HMAC-SHA256(salt, …)[0:16]`. */
export async function smsDigestLeaf(salt: Uint8Array, row: SmsDigestRow): Promise<Uint8Array> {
  assertSalt(salt);
  const mac = await hmacSha256(salt, smsDigestLeafInput(row));
  // FIRST 16 bytes. Pinned by a vector that also carries the full 32, so taking the wrong end is
  // caught rather than merely being a different-looking constant.
  return mac.slice(0, SMS_DIGEST_LEAF_BYTES);
}

/**
 * §3.3: `bucket(set) = XOR over leaf(row)`.
 *
 * Order-independent and incremental, which is the property that lets each side fold its rows in
 * whatever order it holds them. Two consequences worth stating because they are not bugs:
 * an EMPTY set folds to 16 zero bytes, and an identical pair CANCELS to zero. The second is
 * reachable — a duplicate leaf is possible across an epoch rotation — and is a known limit of XOR
 * rather than something this function should defend against.
 */
export function smsDigestBucket(leaves: readonly Uint8Array[]): Uint8Array {
  const out = new Uint8Array(SMS_DIGEST_LEAF_BYTES);
  for (const leaf of leaves) {
    if (leaf.length !== SMS_DIGEST_LEAF_BYTES) {
      throw new RangeError(
        `sms digest: a leaf must be ${SMS_DIGEST_LEAF_BYTES} bytes, got ${leaf.length}`
      );
    }
    for (let i = 0; i < SMS_DIGEST_LEAF_BYTES; i += 1) {
      out[i] ^= leaf[i] as number;
    }
  }
  return out;
}

/**
 * One child of a digest subtree: a key the PC can ask about, and that child's bucket.
 *
 * The KEY is always the value a follow-up request carries verbatim — `""` at root, a provider
 * `THREAD_ID` at thread level, a decimal month key at month level, a decimal day key at day level.
 * Never a hash: a one-way key would make a subtree the phone has and the PC does not both
 * detectable and unaddressable, which is the never-converging descent this whole regime exists to
 * prevent.
 */
export interface SmsDigestEntry {
  readonly key: string;
  readonly bucket: Uint8Array;
}

/**
 * Pack entries for `SmsDigestReplyPayload.entries`.
 *
 *     entry   = be32(len(keyUtf8)) ‖ keyUtf8 ‖ bucket[16]
 *     entries = entry*
 *
 * Self-delimiting, for the same reason the leaf input is: a separator would need a byte no key can
 * contain, and a key is a free-form string from the provider. Length-prefixing does not care.
 *
 * The bucket is the FULL 16 bytes. §3.3 sketched a 14-byte thread entry, which cannot hold a
 * 16-byte bucket alongside any key at all — the resolution is a wider entry, not a shorter bucket.
 * One truncation rule for the whole algorithm means no second constant for two languages to
 * disagree about, and narrowing the bucket would weaken the only thing a bucket does: a missed
 * difference is a divergence nobody ever repairs.
 */
export function packSmsDigestEntries(entries: readonly SmsDigestEntry[]): Uint8Array {
  const parts: Uint8Array[] = [];
  let total = 0;
  for (const entry of entries) {
    if (entry.bucket.length !== SMS_DIGEST_LEAF_BYTES) {
      throw new RangeError(
        `sms digest: a bucket must be ${SMS_DIGEST_LEAF_BYTES} bytes, got ${entry.bucket.length}`
      );
    }
    const keyBytes = utf8(entry.key);
    const packed = new Uint8Array(4 + keyBytes.length + SMS_DIGEST_LEAF_BYTES);
    writeBe64Length(packed, 0, keyBytes.length);
    packed.set(keyBytes, 4);
    packed.set(entry.bucket, 4 + keyBytes.length);
    parts.push(packed);
    total += packed.length;
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

/**
 * Unpack what {@link packSmsDigestEntries} produced.
 *
 * REFUSES a truncated or over-long buffer rather than returning what it managed to read. A digest
 * that parsed "most of" a reply would compare a subset against a full local set and report a
 * divergence that does not exist — and then repair it, which here means deleting.
 */
export function unpackSmsDigestEntries(packed: Uint8Array): SmsDigestEntry[] {
  const entries: SmsDigestEntry[] = [];
  let at = 0;
  while (at < packed.length) {
    if (at + 4 > packed.length) {
      throw new RangeError("sms digest: entries truncated in a key length");
    }
    const keyLength =
      ((packed[at] as number) << 24) |
      ((packed[at + 1] as number) << 16) |
      ((packed[at + 2] as number) << 8) |
      (packed[at + 3] as number);
    if (keyLength < 0) throw new RangeError("sms digest: entries carry a negative key length");
    at += 4;
    const end = at + keyLength + SMS_DIGEST_LEAF_BYTES;
    if (end > packed.length) {
      throw new RangeError("sms digest: entries truncated in an entry body");
    }
    entries.push({
      key: decodeUtf8(packed.subarray(at, at + keyLength)),
      bucket: packed.slice(at + keyLength, end),
    });
    at = end;
  }
  return entries;
}

/**
 * Decode UTF-8 without `TextDecoder`, which `packages/core` cannot declare (`lib: ["ES2020"]`, no
 * DOM, no `@types/node`) — the same constraint that puts `utf8` above in this file.
 *
 * REFUSES malformed input, matching `utf8`'s refusal of an unpaired surrogate. A key that does not
 * round-trip is a key the PC cannot send back, and substituting a replacement character would
 * silently produce one.
 */
function decodeUtf8(bytes: Uint8Array): string {
  let out = "";
  let i = 0;
  while (i < bytes.length) {
    const b0 = bytes[i] as number;
    let code: number;
    let width: number;
    if (b0 < 0x80) {
      code = b0;
      width = 1;
    } else if (b0 >= 0xc2 && b0 <= 0xdf) {
      code = b0 & 0x1f;
      width = 2;
    } else if (b0 >= 0xe0 && b0 <= 0xef) {
      code = b0 & 0x0f;
      width = 3;
    } else if (b0 >= 0xf0 && b0 <= 0xf4) {
      code = b0 & 0x07;
      width = 4;
    } else {
      throw new RangeError("sms digest: entries carry a malformed UTF-8 key");
    }
    if (i + width > bytes.length) {
      throw new RangeError("sms digest: entries carry a truncated UTF-8 key");
    }
    for (let k = 1; k < width; k += 1) {
      const bk = bytes[i + k] as number;
      if ((bk & 0xc0) !== 0x80) {
        throw new RangeError("sms digest: entries carry a malformed UTF-8 key");
      }
      code = (code << 6) | (bk & 0x3f);
    }
    // Reject overlongs, surrogates and out-of-range scalars — the three ways a decoder that
    // accepts "close enough" stops round-tripping.
    if (
      (width === 2 && code < 0x80) ||
      (width === 3 && code < 0x800) ||
      (width === 4 && code < 0x10000) ||
      (code >= 0xd800 && code <= 0xdfff) ||
      code > 0x10ffff
    ) {
      throw new RangeError("sms digest: entries carry a malformed UTF-8 key");
    }
    if (code > 0xffff) {
      const v = code - 0x10000;
      out += String.fromCharCode(0xd800 + (v >> 10), 0xdc00 + (v & 0x3ff));
    } else {
      out += String.fromCharCode(code);
    }
    i += width;
  }
  return out;
}

/**
 * The UTC day a row buckets into, as a signed epoch-day integer.
 *
 * FLOOR, not truncation, and the difference is only visible before 1970 — where `Math.trunc` and
 * Kotlin's `/` both round toward zero while `Math.floor` does not. A pre-1970 provider `date` is
 * absurd but admissible (the guard checks only `typeof === "number"`), and "absurd but admissible"
 * is exactly the input class that produces a permanent one-bucket mismatch nobody can explain.
 * Kotlin must use `floorDiv`.
 *
 * Not hashed: bucket keys address the tree, they are not leaf terms, so this does not enter the
 * frozen bytes. It is pinned by tests anyway, because the two sides must agree on which bucket a
 * row lands in or they compare different sets.
 */
export function smsDigestUtcDay(epochMs: number): number {
  return Math.floor(epochMs / MS_PER_DAY);
}

/**
 * The UTC month a row buckets into, as `year * 12 + monthIndex` with a ZERO-BASED month.
 *
 * Returned as a single integer rather than a `(year, month)` pair so there is no chance of the two
 * languages disagreeing about the base: JavaScript's `getUTCMonth()` is 0-based while Kotlin's
 * `Calendar.MONTH` is 0-based but `LocalDate.monthValue` is 1-based. Collapsing to one number makes
 * the base a property of this function rather than of each caller.
 */
export function smsDigestUtcMonth(epochMs: number): number {
  const date = new Date(epochMs);
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
}
